import classNames from 'classnames'
import Button from 'react-bootstrap/Button'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'

import archidektSvg from '@/assets/archidekt.svg'
import Card from '@/components/Card/Card'
import CardHover from '@/components/CardHover/CardHover'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import toastService from '@/services/toast.service'
import { CommandersPageProps } from '@/types/page'
import { tcgplayerLink } from '@/utils/helpers'

import styles from './DecklistPanel.module.scss'

type Props = {
  data: CommandersPageProps['data']
}

const CATEGORY_ORDER = [
  'commander',
  'creature',
  'instant',
  'sorcery',
  'artifact',
  'enchantment',
  'battle',
  'planeswalker',
  'land',
]

const CATEGORY_LABEL = {
  Artifact: { plural: 'Artifacts' },
  Battle: { plural: 'Battles' },
  Commander: { plural: 'Commanders' },
  Creature: { plural: 'Creatures' },
  Enchantment: { plural: 'Enchantments' },
  Instant: { plural: 'Instants' },
  Land: { plural: 'Lands' },
  Planeswalker: { plural: 'Planeswalkers' },
  Sorcery: { plural: 'Sorceries' },
}

const generateCategoryLabel = (category: string, num: number, hideSingle: boolean = false) =>
  `${num === 1 ? category : category in CATEGORY_LABEL ? CATEGORY_LABEL[category as keyof typeof CATEGORY_LABEL]?.plural : category}${!hideSingle ? ` (${num})` : ''}`

const DecklistPanel = ({ data }: Props) => {
  const { archidekt, deck } = data

  if (!deck) return null

  let deckList: string[] = []
  let title = ''
  if (Array.isArray(deck)) {
    deckList = deck
  } else {
    deckList = Object.keys(deck.cards).reduce<string[]>(
      (acc, card) => {
        deck.cards[card].forEach((card) => acc.push(`${card?.[1]} ${card?.[0]}`))
        return acc
      },
      [`1 ${deck.commander}`],
    )
    title = deck?.name || ''
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(deckList.join('\r\n'))
    toastService.createToast('Copied to clipboard!')
  }

  const archidektDeckbuilderUrl =
    archidekt && `https://archidekt.com/sandbox?deck=${encodeURI(JSON.stringify(archidekt))}`

  const archidektPlaytesterUrl =
    archidekt && `https://archidekt.com/playtester-v2/sandbox?deck=${encodeURI(JSON.stringify(archidekt))}`

  const moxfieldUrl = deck && `https://www.moxfield.com/import?c=${encodeURI(deckList.join('\n'))}`

  const cardkingdomQueriesArray = {
    partner: 'edhrec',
    utm_campaign: 'edhrec',
    utm_medium: 'avgdeck',
    utm_source: 'edhrec',
  }
  let cardkingdomUrl = ''
  if (title) {
    const cardkingdomQueries = new URLSearchParams({
      ...cardkingdomQueriesArray,
      'filter[name]': title,
      'filter[tab]': 'product',
    })
    cardkingdomUrl = 'https://www.cardkingdom.com/catalog/search?' + cardkingdomQueries.toString()
  } else {
    const cardkingdomQueries = new URLSearchParams({
      c: deckList.join('\r\n'),
      ...cardkingdomQueriesArray,
    })
    cardkingdomUrl = 'https://www.cardkingdom.com/builder?' + cardkingdomQueries.toString()
  }

  let tcgplayerUrl = ''
  if (title) {
    const tcgpParams = new URLSearchParams({
      partner: 'EDHREC',
      ProductTypeName: 'Sealed Products',
      q: title,
    })
    tcgplayerUrl = tcgplayerLink({
      subId1: 'edhrec,decklist',
      u: 'https://www.tcgplayer.com/search/all/product?' + tcgpParams.toString(),
    })
  } else {
    const tcgpParams = new URLSearchParams({
      c: deckList.join('||'),
    })
    tcgplayerUrl = tcgplayerLink({
      subId1: 'edhrec,decklist',
      u: 'https://store.tcgplayer.com/massentry?' + tcgpParams.toString(),
    })
  }

  return (
    <div className='p-3'>
      <div className='d-flex justify-content-between mb-3 w-100'>
        <h3 className='me-2'>
          {title ? title : <span>Average</span>} <span>Decklist</span>
        </h3>
        <DropdownButton size='sm' title='Export' variant='secondary'>
          <Dropdown.Item href={archidektDeckbuilderUrl} target='_blank'>
            Archidekt
          </Dropdown.Item>
          <Dropdown.Item href={moxfieldUrl} target='_blank'>
            Moxfield
          </Dropdown.Item>
          <Dropdown.Item onClick={copyToClipboard} target='_blank'>
            Plaintext
          </Dropdown.Item>
        </DropdownButton>
      </div>
      <div className={styles.decklist}>
        <div className='justify-content-center w-100 gap-4 d-md-flex'>
          <div className={styles.sidebar}>
            <div className={styles.sidebarContent}>
              {data.precon_image ? (
                <div className={styles.imageWrapper}>
                  <img alt={`Image of the ${title} sealed product`} src={data.precon_image} />
                </div>
              ) : !Array.isArray(deck) ? (
                deck.commander.map((commander) => (
                  <div className='mb-2' key={commander}>
                    <Card name={commander} />
                  </div>
                ))
              ) : (
                <div className='mb-2'>
                  <Card name={deck?.[0]?.split(' ')?.slice(1)?.join(' ')} />
                </div>
              )}
              <div className={styles.buyLinks}>
                {cardkingdomUrl && (
                  <OutboundLink className={styles.buyLink} url={cardkingdomUrl}>
                    <div className='d-flex flex-row align-items-center mb-1'>
                      <svg
                        height='12'
                        version='1.1'
                        viewBox='0 0 3.7041667 3.1750001'
                        width='14'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <g transform='translate(0 -293.82)'>
                          <path d='m0 293.82v1.1592h0.52042v2.0158h2.6633v-2.0158h0.52042v-1.1592h-0.57636v1.0908h-0.49196v-1.084h-0.54839v1.084h-0.47074v-1.084h-0.54839v1.084h-0.49196v-1.0908z' />
                        </g>
                      </svg>
                      <span className='ms-1'>Buy at Card Kingdom</span>
                    </div>
                  </OutboundLink>
                )}
                {tcgplayerUrl && (
                  <OutboundLink className={styles.buyLink} url={tcgplayerUrl}>
                    <div className='d-flex flex-row align-items-center'>
                      <svg height='12' id='svg708' version='1.1' viewBox='0 0 16 12' width='16.271'>
                        <g strokeWidth='0px'>
                          <path d='m3.1428 1.0847-2.3024 0.34169c-0.53966 0.078644-0.90847 0.57763-0.82983 1.1119l1.1498 7.6746c0.081356 0.53424 0.58034 0.90305 1.12 0.82441l0.86237-0.12746z' />
                          <path d='m15.43 1.4264-2.2725-0.33627v9.8251l0.83254 0.12474c0.53695 0.07864 1.0386-0.29017 1.12-0.82441l1.1498-7.6746c0.08136-0.53424-0.29017-1.0332-0.82983-1.1119z' />
                          <path d='m5.079 3.4712' />
                          <path d='m5.079 3.4685' />
                          <path
                            d='m4.7455 0h6.8122c0.54508 0 0.98441 0.43932 0.98441 0.97898v10.042c0 0.54237-0.44203 0.97898-0.98441 0.97898h-6.8122c-0.54508 0-0.98441-0.43932-0.98441-0.97898v-10.042c0-0.54237 0.44203-0.97898 0.98441-0.97898zm4.1383 4.8759h2.0692c0.19797 0 0.31458 0.22237 0.20339 0.38508l-2.9451 4.2766c-0.1383 0.19797-0.45017 0.10305-0.45017-0.13831v-2.2183h-2.4136c-0.1817 0-0.30102-0.18983-0.22237-0.35254l2.0339-4.1763c0.040678-0.084068 0.12746-0.13831 0.22237-0.13831h2.259c0.1817 0 0.30102 0.18983 0.22237 0.35254z'
                            fillRule='evenodd'
                          />
                        </g>
                      </svg>
                      <span className='ms-1'>Buy at TCGPlayer</span>
                    </div>
                  </OutboundLink>
                )}
              </div>
              <div className='d-grid'>
                <Button className={styles.archidekt} href={archidektPlaytesterUrl} target='_blank'>
                  <img alt='Archidekt' src={archidektSvg.src} />
                  <span>Playtest on Archidekt</span>
                </Button>
              </div>
            </div>
          </div>
          <ul className={classNames([styles.deckGroups, Array.isArray(deck) ? styles.noMargin : ''])}>
            {Array.isArray(deck) ? (
              <li className={styles.breakable}>
                <ul>
                  {deck.map((card) => (
                    <li key={card}>
                      <strong>{card.substring(0, card.indexOf(' '))}</strong>{' '}
                      <span>
                        <CardHover name={card.substring(card.indexOf(' ') + 1)} />
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <>
                <li>
                  <h3 className='mb-2'>{generateCategoryLabel('Commander', deck.commander.length, true)}</h3>
                  <ul>
                    {deck.commander.map((commander) => (
                      <li key={commander}>
                        <strong>1</strong>
                        <CardHover name={commander} />
                      </li>
                    ))}
                  </ul>
                </li>
                {Object.keys(deck.cards)
                  .sort((a, b) => CATEGORY_ORDER.indexOf(a.toLowerCase()) - CATEGORY_ORDER.indexOf(b.toLowerCase()))
                  .map((category) => (
                    <li key={category}>
                      <h3 className='mb-2'>
                        {generateCategoryLabel(
                          category,
                          deck.cards[category].reduce((acc, card) => acc + card[1], 0),
                        )}
                      </h3>
                      <ul>
                        {deck.cards[category].map((card) => (
                          <li key={card[0]}>
                            <strong>{card[1] || 1}</strong>
                            <span>
                              <CardHover name={card[0]} />
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default DecklistPanel
